import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react'
import { Form, Row, Col, Collapse, Tooltip, Descriptions, Table, Button, Space, Message, Tabs } from '@bedrock/components'
import { Delete, PlusCircle, Plus, Copy2, NoticeTextRight, ChevronRight, SignIn } from '@bedrock/icons-react'
import { isEqual } from "lodash";
import { useControllableValue, useMemoizedFn } from "ahooks";
import classNames from "classnames/bind";
import styles from "./index.less";
import KPITable, { KPITableProps } from "#features/KPITable";
import SFromItem from "#components/SForm/SFormItem";
import CollapseTablePanel from "#components/CollapseTablePanel";
import FormItemDescriptions, {
    FormItemDescriptionsItemType
} from "#components/DescriptionsFormItem";
import {
    commonGetDemandCooperateProperties,
    PropertyRefDto,
    DemandCooperatePropertyValueDto,
    DemandKpiValueDto,
    commonFilterOptions,
    commonGetDemandCooperateProperties2,
    DemandCooperatePropertiesVo
} from "@mpc/common/src/services/api";
import { useImportFileModal } from "#features/importFileModal";
import CooperationPropertyForm from "#features/CooperationFormList/CooperationPropertyForm";
import OvertimeTag from '#features/OvertimeTag';
import { orderNum } from "#utils/number";
import ActualExecutionTag from '#features/ActualExecutionTag';
import PriceDetailModal from './PriceDetailModal';
import CooperationDetails from './CooperationDetails';
import CurrencyType from '#components/SForm/SFormElement/CurrencyType';
import { $sType } from '#utils';
import EquityFormTable, { getExpectRightsLabel, getFormItemKey } from '@mpc/common/src/features/EquityFormTable';
import { cloneDeep } from 'lodash';
import TravelTag from '#features/TravelTag';
import { getInnerStructuredSersion } from '#features/utils';
import ClosingReportBtn from '#features/MPCDemand/ClosingReportTable/ClosingReportBtn';
import NoStructuredCooperation from './NoStructuredCooperation';

const cx = classNames.bind(styles)

const DEFAULT_ACTIVE_KEYS = Array.from({length: 20}, (_, i) => i);
const ALL_ACTIVE_KEYS = Array.from({length: 1000}, (_, i) => i);

export type CooperationValueItem = {
    id?: number;
    /** 备注 */
    remark?: string;
    /** 是否实际执行 */
    need_actual_execution: boolean;
    /** 【结构化】合作内容属性值 */
    property_values?: DemandCooperatePropertyValueDto[];
    /** 【结构化】kpi指标列表 */
    kpi_values?: DemandKpiValueDto[];
    /** 是否包含含差旅餐饮等实报实销 */
    contain_travel_expenses_reimbursement?: boolean;
}

type CooperationFormListProps = {
    category_id: number; // 需求ID
    hasKPI?: boolean; // 是否有KPI
    view?: boolean; // 是否是编辑类型
    value?: CooperationValueItem[];
    onChange?: (value: any[]) => void;
    kpiTableProps?: KPITableProps; // KPI属性
    depth?: number;
    inPlanning: boolean; // 是否为策划信息
    addable?: boolean; // 是否可添加
    deletable?: boolean; // 是否可删除
    /**
     * 编辑类型
     * default: 默认编辑
     * returnToBusiness: 退回商务确认报价节点（策划编辑）
     */
    editType?: 'default' | 'returnToBusiness'; // 编辑类型
    demand_supplier_quotes?: any
    detail?: any;
    onImportSuccess?: () => void;
    schema?: any
    tabsModel?: boolean; // 是否是tabs模式
    updateReleaseTime?: (values: any, add?: boolean) => void; // 更新发布时间
}

const CooperationFormList = forwardRef(function (props: CooperationFormListProps, ref) {
    const [dataList, setDataList] = useControllableValue<CooperationValueItem[]>(props, {
        defaultValue: []
    })
    const {
        category_id,
        view = false,
        hasKPI,
        kpiTableProps = {},
        depth, addable = true,
        deletable = true,
        editType = 'default',
        demand_supplier_quotes = [],
        detail,
        onImportSuccess,
        schema,
        tabsModel,
        updateReleaseTime
    } = props
    const [activeKeys, setActiveKeys] = useState<number[]>(tabsModel ? ALL_ACTIVE_KEYS : DEFAULT_ACTIVE_KEYS);
    const [activeTabKey, setActiveTabKey] = useState('0');

    const [form] = Form.useForm();

    // 外部数据变化，更新本地数据
    const [localDataList, setLocalDataList] = useState([])
    useEffect(() => {
        if (isEqual(dataList, localDataList)) {
            return
        } else {
            form.setFieldsValue({
                list: dataList
            })
        }
    }, [dataList, localDataList]);

    useImperativeHandle(ref, () => ({
        validateFields: () => {
            // 缓执行，解决动态校验逻辑，导致新规则无法及时生效问题
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    form.clearValidate();
                    form.validateFields()
                        .then(() => {
                            resolve(undefined)
                        })
                        .catch((error) => {
                            return reject(undefined)
                        })
                }, 10)
            })
        },
    }))


    const { openImportFileModal, closeImportFileModal } = useImportFileModal({});


    const onFormChange = useMemoizedFn((changedValues, allValues) => {
        setDataList(allValues.list)
        setLocalDataList(allValues.list)
    })
    const isSingle = dataList.length <= 1;

    const handleApplyToTheWholeOrder = (key, value) => {
        const newDataList = dataList.map((item, index) => ({
            ...item,
            [key]: value
        }))
        setDataList(newDataList);
        Message.success('应用成功，已覆盖填写到全部合作内容')
    }

    return (<Form
        className={cx('mpc-common-cooperate-form-list')}
        form={form}
        onValuesChange={onFormChange}
        initialValues={{
            list: [{}]
        }}
        style={{ width: '100%' }}
    >
        <Form.List
            name="list"
        >
            {(fields, { add, remove }, { errors }) => {
                const hasError = errors && !!errors.length;
                return (
                    <>
                      {!view && addable && detail?.structured && <div style={{ position: 'relative' }}>
                        <Button
                            type="checked-neutral"
                            icon={<SignIn />}
                            className={cx('import-file-btn')}
                            onClick={() => {
                                openImportFileModal({
                                    importType: `demand_cooperate_planner_${category_id}`,
                                    importIsMemory: false,
                                    uploadFinishedCallback: (res) => {
                                        // 有错误时，不进行数据更新
                                        if (res?.data?.fail_count > 0) return;
                                        if (res.code === 1 && res.data?.data?.length) {
                                            closeImportFileModal();
                                            const list = res.data?.data?.map(v => v?.demand_cooperate_account_dto);
                                            Message.success(`导入覆盖成功 ${list.length} 数据`)
                                            setDataList(list)
                                            onImportSuccess?.();
                                        }
                                    },
                                })
                            }}
                        >
                            批量导入合作内容
                        </Button>
                      </div>}
                        {tabsModel && <Tabs activeKey={activeTabKey} type="card" onChange={(v) => {setActiveTabKey(v)}} className='m-b-8'>
                            {
                                fields.map((field, index) => {
                                    return <Tabs.TabPane tab={`合作内容${orderNum(dataList?.[index]?.$orderIndex, index)}`} key={index + ''}></Tabs.TabPane>
                                })
                            }
                        </Tabs>}
                        {fields.map((field, index) => {
                            const { key, name, ...fieldRest } = field;
                            const fieldConfig = (fieldType: string) => {
                                return {
                                    formItemKey: [name, fieldType],
                                    name: [name, fieldType],
                                    fieldKey: [key, fieldType],
                                    view,
                                    ...fieldRest
                                }
                            }

                            const { delay_grade, delay_label, $orderIndex } = dataList?.[index] || {};
                            const cooperate_account_id = dataList?.[index]?.id;
                            const demand_supplier_quote = demand_supplier_quotes?.find?.(v => v.cooperate_account_id === cooperate_account_id);
                          const title = `合作内容${orderNum($orderIndex, index)}`

                          if (tabsModel && index !== Number(activeTabKey)) {
                                return null;
                            }

                            return (
                                <Collapse
                                    // defaultActiveKey={[1]}
                                    activeKey={activeKeys.includes(index) ? [1] : undefined}
                                    key={index}
                                    onChange={(key) => {
                                        setActiveKeys(activeKeys.includes(index) ? activeKeys.filter((i) => i !== index) : [...activeKeys, index])
                                    }}
                                >
                                    <CollapseTablePanel
                                        className={OvertimeTag.conf?.[delay_grade]?.panelHeaderbgClassName}
                                        // showArrow={false}
                                        style={{ width: '100%' }}
                                        header={<div
                                            className={cx('inner-header')}
                                        >
                                            <div>
                                                <Space size={4}>
                                                    <span>{title}</span>
                                                    {depth === 2 && !props.inPlanning && <ActualExecutionTag value={dataList[index]?.need_actual_execution} />}
                                                    {!props.inPlanning && <TravelTag
                                                        value={demand_supplier_quote?.contain_travel_expenses_reimbursement}
                                                        need_actual_execution={demand_supplier_quote?.need_actual_execution}
                                                        detail={detail}
                                                    />}
                                                    {!props.inPlanning && <OvertimeTag delay_grade={delay_grade} delay_label={delay_label} className='' />}
                                                </Space>
                                            </div>
                                            {!view && <Space
                                                size={0}
                                            >
                                                {addable &&
                                                    <Button type="text-subtle" size="xSmall" icon={<Copy2 />}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                const listData = form.getFieldValue('list')
                                                                const copyData = JSON.parse(JSON.stringify(listData[index]))
                                                                add(copyData, index + 1)
                                                            }}
                                                    >
                                                        复制
                                                    </Button>
                                                }

                                                {deletable &&
                                                    <Button disabled={isSingle} type="text-subtle" size="xSmall" icon={<Delete />}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                remove(field.name);
                                                            }}
                                                            danger
                                                    >
                                                            删除
                                                    </Button>
                                                }
                                            </Space>}
                                            {detail?.need_cooperate_account_supplier_settle_price && !props.inPlanning && <div>
                                                <span className={cx('settle-title')}>最终结算金额：</span>
                                                <CurrencyType value={dataList?.[index]?.$supplier_settle_price} view showAmountOrder={false} />
                                            </div>}
                                        </div>}
                                        key={1}>
                                        {!view && detail?.structured && <SFromItem
                                            {...fieldConfig('property_values')}
                                            showLabel={false}
                                            validateTrigger={false}
                                            watchChildFormValidate={true}
                                            className={cx('property_values_from_item')}
                                            render={() => {
                                                return <CooperationPropertyForm
                                                    view={view}
                                                    schema={schema}
                                                    editType={props.editType}
                                                    structuredVersion={getInnerStructuredSersion(detail, view)}
                                                />
                                            }}
                                        />}
                                        {detail?.structured && <FormItemDescriptions
                                            lightBorder
                                            className={cx('collapse-content-table')}
                                            column={2}
                                            items={[
                                                {
                                                    ...fieldConfig('property_values'),
                                                    label: '合作内容',
                                                    validateTrigger:false,
                                                    watchChildFormValidate: true,
                                                    span: 2,
                                                    hide: !view || !dataList?.[index]?.property_values?.length,
                                                    render:() => {
                                                        return <CooperationDetails index_id={orderNum($orderIndex, index)}/>
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('kpi_values'),
                                                    label: '考核指标',
                                                    view: (() => {
                                                        // 判断是否有编辑权限,单个编辑，否则无法校验
                                                        const hasColEdit = kpiTableProps?.colKeys?.some?.((n) => n.view === false)
                                                        if (hasColEdit) return false;
                                                        return view
                                                    })(),
                                                    required: !!kpiTableProps?.required,
                                                    span: 2,
                                                    watchChildFormValidate: true,
                                                    validateTrigger: false,
                                                    hide: !hasKPI || (detail?.need_cooperate_account_supplier_settle_price && dataList[index]?.need_actual_execution === false),
                                                    render: (renderData, itemProps) => {
                                                        // 是否实际执行为否则KPI不可编辑
                                                        const kpiView = dataList[index]?.need_actual_execution === false ? true : view;
                                                        return <KPITable
                                                            need_actual_execution={dataList[index]?.need_actual_execution}
                                                            view={kpiView}
                                                            required={false}
                                                            platforms={['MPC']}
                                                            emptyRender={() => {
                                                                return <div>--</div>
                                                            }}
                                                            $supplier_settle_price={dataList[index]?.$supplier_settle_price}
                                                            cooperate_account_id={cooperate_account_id}
                                                            need_cooperate_account_supplier_settle_price={detail?.need_cooperate_account_supplier_settle_price}
                                                            applyToTheWholeOrder={!view && addable && detail?.structured}
                                                            handleApplyToTheWholeOrder={(value) => handleApplyToTheWholeOrder('kpi_values', value)}
                                                            {...kpiTableProps}
                                                        />
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('necessary_rights'),
                                                    label: '必要权益',
                                                    span: 2,
                                                    hide: detail?.structured_version < 1 && !['pending_design','underway'].includes(detail?.state),
                                                    required: true,
                                                    watchChildFormValidate: true,
                                                    validateTrigger: false,
                                                    render: () => {
                                                        return <EquityFormTable
                                                            key='necessary_rights' 
                                                            formItemKey='necessary_rights' 
                                                            detail={detail} 
                                                            view={view} 
                                                            inPlanning={props.inPlanning}
                                                            rightsList={schema?.necessary_rights_list}
                                                            applyToTheWholeOrder={!view && addable && detail?.structured}
                                                            handleApplyToTheWholeOrder={(value) => handleApplyToTheWholeOrder('necessary_rights', value)}
                                                        />
                                                    }
                                                },
                                                {
                                                    ...fieldConfig(getFormItemKey(detail, {inPlanning: props.inPlanning, view})),
                                                    label: getExpectRightsLabel(getFormItemKey(detail, {inPlanning: props.inPlanning, view})),
                                                    span: 2,
                                                    hide: detail?.structured_version < 1 && !['pending_design','underway'].includes(detail?.state),
                                                    watchChildFormValidate: true,
                                                    validateTrigger: false,
                                                    render: () => {
                                                        return <EquityFormTable
                                                                key='expect_rights'
                                                                formItemKey={getFormItemKey(detail, {inPlanning: props.inPlanning, view})}
                                                                detail={detail}
                                                                inPlanning={props.inPlanning}
                                                                view={view}
                                                                rightsList={schema?.expect_rights_list}
                                                                applyToTheWholeOrder={!view && addable && detail?.structured}
                                                                handleApplyToTheWholeOrder={(value) => handleApplyToTheWholeOrder(getFormItemKey(detail, {inPlanning: props.inPlanning, view}), value)}
                                                            />
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('remark'),
                                                    label: '备注',
                                                    required: false,
                                                    componentClass: "textarea",
                                                    span: 2,
                                                    elementProps: {
                                                        maxLength: 500,
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('discount_quote_price_with_tax'),
                                                    label: '报价金额-变更后(含税 + 服务费)',
                                                    span: 1,
                                                    hide: !detail?.need_cooperate_account_supplier_settle_price || props.inPlanning,
                                                    render: () => {
                                                        return <PriceDetailModal
                                                            demand_supplier_quote={demand_supplier_quote}
                                                            detail={detail}
                                                            orderNum={orderNum($orderIndex, index)}
                                                        />
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('$supplier_settle_price'),
                                                    label: '最终结算金额(含税 + 服务费)',
                                                    span: 1,
                                                    componentClass: 'currencyType',
                                                    view: $sType === 'inner' || dataList[index]?.need_actual_execution === false,
                                                    required: true,
                                                    hide: !detail?.need_cooperate_account_supplier_settle_price || props.inPlanning,
                                                    elementProps: {
                                                        selectDisabled: true,
                                                        // max: demand_supplier_quote?.discount_quote_price_with_tax,
                                                        // formatter: undefined,
                                                    },
                                                    rules:
                                                        [
                                                            {
                                                                message: '金额需 ≤ 报价金额-变更后（含税+服务费）',
                                                                validator: function (_, value) {
                                                                    return new Promise((resolve, reject) => {
                                                                        if (value.value > demand_supplier_quote?.discount_quote_price_with_tax) {
                                                                            reject(new Error('金额需 ≤ 报价金额-变更后（含税+服务费）'));
                                                                        } else {
                                                                            resolve('');
                                                                        }
                                                                    });
                                                                },
                                                            }
                                                        ]

                                                },
                                                {
                                                    ...fieldConfig('case_close_detail'),
                                                    label: '结案明细',
                                                    span: 2,
                                                    view: true,
                                                    hide: !(detail?.need_structured_settle_report && (detail?.depth === 2) && demand_supplier_quote?.need_actual_execution && !props.inPlanning),
                                                    elementProps: ({ optionMap, detailData }) => {
                                                        return {
                                                            showComponent: (value) => {
                                                                return (
                                                                    <ClosingReportBtn detail={detail} view={view} cooperateAccountId={cooperate_account_id} />
                                                                )
                                                            }
                                                        }
                                                    }
                                                },
                                                {
                                                    ...fieldConfig('case_close_report_cooperate_account_attachments'),
                                                    label: '结案报告 (差旅、餐补)',
                                                    span: 2,
                                                    required: !!demand_supplier_quote?.contain_travel_expenses_reimbursement,
                                                    view: detail?.state !== 'making',
                                                    hide: (detail?.depth !== 2 || props.inPlanning || !detail?.$contentStructured || !detail?.is_screenshot_done)
                                                        ? true : !demand_supplier_quote?.need_actual_execution,
                                                    componentClass: 'upload',
                                                    inputExtra: !!demand_supplier_quote?.contain_travel_expenses_reimbursement && detail?.state === 'making' && (
                                                        <span className={cx('case-close-report-tip')}>注：当前合作内容包含差旅餐饮等结算项，需上传对应结案证明，以供实报实销结算。</span>
                                                    )
                                                },
                                            ].filter(n => !n.hide)}
                                        />}
                                        {!detail?.structured && <NoStructuredCooperation fieldConfig={fieldConfig}/>}
                                    </CollapseTablePanel>
                                </Collapse>
                            );
                        })}
                        {!view && addable && <>
                            <Button type="checked" icon={<Plus />}
                                    onClick={() => {
                                        add({});
                                        setTimeout(() => {
                                            updateReleaseTime({} , true)
                                        }, 500);
                                    }}
                                    style={{width: '100%'}}
                                    size='large'
                            >
                                添加合作内容
                            </Button>
                        </>}
                    </>
                );
            }}
        </Form.List>
    </Form>)
})

export default CooperationFormList;
